import { InputLabel, MenuItem } from '@mui/material';
import { memo, useMemo } from 'react';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValoreDominioVO } from 'shell/models/act-wizard';
import { HttpResponse } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';

interface ListAnscProps {
  disabled?: boolean;
  required?: boolean;
  value: string;
  onChange: (value?: ItemVM) => void;
  error?: boolean;
  isSmallSize?: boolean;
  idDominio: string;
  label: string;
  betweenFrom?: number;
  betweenTo?: number;
  onlyId?: Array<string>; // array dei soli ID da mostrare
  withoutId?: Array<string>; // array degli ID da NON mostrare
  filter?: (items: ItemVM[]) => ItemVM[];
  tipoEvento?: string;
  itemsToAdd?: Array<ItemVM>;
}

const ListAnscComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  isSmallSize = false,
  idDominio,
  label,
  betweenFrom,
  betweenTo,
  onlyId,
  withoutId,
  filter,
  tipoEvento,
  itemsToAdd = [],
}: ListAnscProps): React.ReactElement => {
  const [anscRes] = useHttpFetch<HttpResponse<Array<ValoreDominioVO>>, Array<ItemVM>>(
    `${__ACT_API_URL__}/filtri/dominio/${idDominio}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) => {
        let castedResponse = (response.data.payload || []) as Array<ValoreDominioVO>;

        if (betweenFrom && betweenTo) {
          castedResponse = castedResponse.filter(
            (item) =>
              Number.parseInt(item.key?.idValore || '', 10) >= betweenFrom &&
              Number.parseInt(item.key?.idValore || '', 10) <= betweenTo
          );
        }

        if (onlyId && onlyId.length) {
          castedResponse = castedResponse.filter((item) => {
            let res = false;

            onlyId.forEach((itemOnlyId) => {
              if (item.key?.idValore?.toString() === itemOnlyId) {
                res = true;
              }
            });

            return res;
          });
        }

        if (withoutId && withoutId.length) {
          castedResponse = castedResponse.filter((item) => {
            let res = false;

            withoutId.forEach((itemOnlyId) => {
              if (item.key?.idValore?.toString() !== itemOnlyId) {
                res = true;
              }
            });

            return res;
          });
        }

        if (tipoEvento) {
          castedResponse = castedResponse.filter((item) => item.codiceStatistico === tipoEvento);
        }

        return castedResponse.map((item) => ({
          id: item.key?.idValore,
          value: item.descrizioneValore,
        })) as Array<ItemVM>;
      },
    }
  );

  const anscResData: Array<ItemVM> = useMemo(() => {
    if (filter) {
      return filter((anscRes.data || []) as Array<ItemVM>);
    }

    let res = (anscRes.data || []) as Array<ItemVM>;

    if (itemsToAdd?.length) {
      res = [...res, ...itemsToAdd];
    }

    return res;
  }, [anscRes.data, itemsToAdd]);

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={`${label}-id`}>
        {required ? `${label} *` : label}
      </InputLabel>
      <PmiSelect
        labelId={`${label}-label`}
        id={label}
        name={label}
        value={value}
        label={required ? `${label} *` : label}
        onChange={(event) => onChange(anscResData?.find((item) => item.id === event.target.value))}
        disabled={disabled}
        error={error}
        size={isSmallSize ? 'small' : undefined}
      >
        {!required ? (
          <MenuItem>
            <em>Nessuno</em>
          </MenuItem>
        ) : undefined}
        {anscResData?.map((condition: ItemVM) => (
          <MenuItem key={condition.id} value={condition.id}>
            {condition.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListAnscComponent.displayName = 'List anscRes component';

const ListAnsc = memo(ListAnscComponent);

export default ListAnsc as typeof ListAnscComponent;
